import React from 'react';
import { useCallback, useContext } from 'react';
import {
  Divider,
  Typography,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Box,
  Paper,
  Alert,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { PDPFormText } from './PDPFormText';
import { clamp } from 'lodash';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { PdpDetailData } from './types';
import { AuthContext, AuthContextType } from 'src/contexts/AuthContext';
import {
  PdpDetail,
  useGetPdpDetailsQuery,
  useSavePdpDetailsMutation,
  useSubmitPdpMutation,
  PdpType,
  PdpStatus,
  CpdActivityPlan
} from 'src/graphql/generated';
import { PdpLoading } from './PdpLoading';

const PDPForm2026Plus = () => {
  const { year } = useParams<{ year: string }>();
  const { activeMemberId } = useContext(AuthContext) as AuthContextType;
  const [pdpSubmitStatus, setPdpSubmitStatus] = React.useState<PdpStatus>();
  const [open, setOpen] = React.useState(false);
  const [openSnack, setOpenSnack] = React.useState(false);
  const [dialogTitle, setDialogTitle] = React.useState<String>('');
  const [dialogDescription, setDialogDescription] = React.useState<String>('');
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };
  const history = useHistory();
  const { register, handleSubmit, control, watch, setValue, reset, formState } =
    useForm<PdpDetailData>({
      defaultValues: {
        current_situation: '',
        goals: '',
        annual_review_and_reflection: '',
        reflection_culturally_save_practice: '',
        reflection_health_inequities: '',
        reflection_professionalism_ethical_practice: '',
        hours_spent: 0,
        attachments: [],
        eaHours: 0,
        eaTags: [],
        rpHours: 0,
        rpTags: [],
        moHours: 0,
        moTags: []
      },
      mode: 'onChange'
    });

  // destructured here because otherwise this does not work on first render
  const { errors, isDirty, isValid } = formState;
  const watchAllFields = watch();
  const nanToZero = (num: number) => {
    if (Number.isNaN(num)) {
      return 0;
    } else return num;
  };
  const roundHalf = (num: number) => {
    return Math.round(num * 2) / 2;
  };
  const roundField = useCallback(
    (fieldName: 'eaHours' | 'rpHours' | 'moHours' | 'hours_spent') => {
      setValue(fieldName, clamp(roundHalf(watch(fieldName)), 0, 500), {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [setValue, watch]
  );

  const { loading, error: errorGetQuery } = useGetPdpDetailsQuery({
    variables: { memberid: activeMemberId, pdp_year: year },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const pdpDetailsData = data?.getPdpDetails as PdpDetail;

      const eaActivityPlan = pdpDetailsData?.cpdActivityPlans?.find(
        (cpdActivityPlanType) => cpdActivityPlanType?.type === 'ea'
      ) as CpdActivityPlan;
      const rpActivityPlan = pdpDetailsData?.cpdActivityPlans?.find(
        (cpdActivityPlanType) => cpdActivityPlanType?.type === 'rp'
      ) as CpdActivityPlan;
      const moActivityPlan = pdpDetailsData?.cpdActivityPlans?.find(
        (cpdActivityPlanType) => cpdActivityPlanType?.type === 'mo'
      ) as CpdActivityPlan;
      setPdpSubmitStatus(pdpDetailsData?.pdp_status as PdpStatus);

      reset({
        current_situation: pdpDetailsData?.current_situation ?? '',
        goals: pdpDetailsData?.goals ?? '',
        reflection_culturally_save_practice:
          pdpDetailsData?.reflection_culturally_save_practice ?? '',
        reflection_health_inequities: pdpDetailsData?.reflection_health_inequities ?? '',
        reflection_professionalism_ethical_practice:
          pdpDetailsData?.reflection_professionalism_ethical_practice ?? '',
        annual_review_and_reflection: pdpDetailsData?.annual_review_and_reflection ?? '',
        eaHours: eaActivityPlan?.hours ?? 0,
        rpHours: rpActivityPlan?.hours ?? 0,
        moHours: moActivityPlan?.hours ?? 0,
        eaTags: eaActivityPlan?.tags || [],
        rpTags: rpActivityPlan?.tags || [],
        moTags: moActivityPlan?.tags || [],
        hours_spent: pdpDetailsData?.hours_spent ?? 0
      });
    }
  });

  const [savePdpDetailsMutation, { error: errorSaveMutation }] = useSavePdpDetailsMutation({
    variables: {
      memberid: activeMemberId,
      pdp_year: year,
      pdp_details: {
        scope_of_practice: '',
        goals: watchAllFields.goals,
        general_comments: '',
        quick_log_reflection_comments: '',
        annual_review_and_reflection: watchAllFields.annual_review_and_reflection,
        commented_activities: [],
        cpd_activity_key: '',
        cpdActivityPlans: [
          {
            type: 'ea',
            tags: watchAllFields.eaTags ?? [],
            hours: watchAllFields.eaHours
          },
          {
            type: 'rp',
            tags: watchAllFields.rpTags ?? [],
            hours: watchAllFields.rpHours
          },
          {
            type: 'mo',
            tags: watchAllFields.moTags ?? [],
            hours: watchAllFields.moHours
          }
        ],
        attachments: [],
        pdp_type: PdpType.Template,
        exempt: false,
        current_situation: watchAllFields.current_situation,
        reflection_culturally_save_practice: watchAllFields.reflection_culturally_save_practice,
        reflection_professionalism_ethical_practice:
          watchAllFields.reflection_professionalism_ethical_practice,
        reflection_health_inequities: watchAllFields.reflection_health_inequities,
        hours_spent: watchAllFields.hours_spent
      }
    },
    onCompleted: () => {
      reset({
        current_situation: watchAllFields.current_situation,
        goals: watchAllFields.goals,
        reflection_culturally_save_practice: watchAllFields.reflection_culturally_save_practice,
        reflection_health_inequities: watchAllFields.reflection_health_inequities,
        reflection_professionalism_ethical_practice:
          watchAllFields.reflection_professionalism_ethical_practice,
        annual_review_and_reflection: watchAllFields.annual_review_and_reflection,
        eaHours: watchAllFields.eaHours,
        rpHours: watchAllFields.rpHours,
        moHours: watchAllFields.moHours,
        eaTags: watchAllFields.eaTags,
        rpTags: watchAllFields.rpTags,
        moTags: watchAllFields.moTags,
        hours_spent: watchAllFields.hours_spent
      });
      if (pdpSubmitStatus === PdpStatus.PdpSubmitted) {
        setOpen(true);
        setDialogTitle('Your PDP has been updated');
        setDialogDescription('Your hours have been added and you can view now in History.');
      }
      if (pdpSubmitStatus === PdpStatus.PdpDraft) {
        setOpenSnack(true);
      }
    },
    onError: () => {}
  });

  React.useEffect(() => {
    const unloadCallback = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', unloadCallback);
    return () => window.removeEventListener('beforeunload', unloadCallback);
  }, [isDirty]);

  const handleClose = () => {
    setOpen(false);
    history.push('/pdp/' + year, { selectedTemplate: 'home' });
  };
  const handleCloseSnack = () => {
    setOpenSnack(false);
  };
  const handleSave = () => {
    savePdpDetailsMutation();
  };
  const onSubmit = () => {
    savePdpDetailsMutation({ onCompleted: () => submitPdpMutation() });
  };

  const [submitPdpMutation, { error: errorSubmitQuery }] = useSubmitPdpMutation({
    variables: {
      memberid: activeMemberId,
      pdp_year: year
    },
    onCompleted: () => {
      setOpen(true);
      setDialogTitle(`Congratulations! You have successfully submitted your PDP for ${year}.`);
      setDialogDescription('Your hours have been added and you can view now in History.');
    }
  });

  React.useEffect(() => {
    if (isDirty && pdpSubmitStatus === PdpStatus.PdpDraft) {
      const interval = setInterval(() => {
        savePdpDetailsMutation();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isDirty, pdpSubmitStatus, reset, savePdpDetailsMutation, watchAllFields]);

  if (loading) {
    return <PdpLoading />;
  }

  if (errorGetQuery) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{errorGetQuery.message}</Alert>
      </Box>
    );
  }
  if (errorSubmitQuery) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{errorSubmitQuery.message}</Alert>
      </Box>
    );
  }
  if (errorSaveMutation) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{errorSaveMutation.message}</Alert>
      </Box>
    );
  }
  return (
    <>
      <PDPFormText />
      <Paper sx={{ p: 3, my: 2 }} elevation={3}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Prompt
            when={isDirty}
            message={() => `You have unsaved changes, are you sure you want to leave?`}
          />
          <Box>
            <Box>
              <Typography
                variant="h6"
                color={errors.current_situation ? 'red' : 'primary'}
                sx={{ mb: 2 }}
              >
                Reflection on last year's learning{' '}
                <Typography
                  variant={'caption'}
                  color={errors.current_situation ? 'red' : 'primary'}
                  fontWeight={400}
                >
                  (Required)
                </Typography>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ pb: 2 }}>
                Based on last year's PDP and CPD activities, how did they improve or change your
                practise? Which activities did you find most useful?
              </Typography>
              <Controller
                name="current_situation"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    {...register('current_situation', { required: true, minLength: 3 })}
                    error={!!error}
                    fullWidth={true}
                    id="current_situation"
                    label="Your response"
                    multiline
                    inputProps={{ maxLength: 1600 }}
                    minRows={5}
                    maxRows={15}
                    variant="outlined"
                  />
                )}
              />
              <Typography component="p" variant="caption" align="right" sx={{ mt: 1 }}>
                {watchAllFields.current_situation?.length}/1600
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 4 }}>
            <Box>
              <Typography variant="h6" color={errors.goals ? 'red' : 'primary'} sx={{ mb: 2 }}>
                Your CPD focus for this year{' '}
                <Typography
                  variant={'caption'}
                  color={errors.goals ? 'red' : 'primary'}
                  fontWeight={400}
                >
                  (Required)
                </Typography>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ pb: 2 }}>
                Briefly outline your learning goals for this year. These may remain the same as last
                year, or you may want to refresh or update your knowledge and/or skills.
              </Typography>
              <Controller
                name="goals"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    {...register('goals', {
                      required: true,
                      minLength: 3
                    })}
                    error={!!error}
                    fullWidth={true}
                    id="outlined-textarea"
                    label="Your response"
                    multiline
                    inputProps={{ maxLength: 1600 }}
                    minRows={5}
                    maxRows={15}
                    variant="outlined"
                  />
                )}
              />
              <Typography component="p" variant="caption" align="right" sx={{ mt: 1 }}>
                {watchAllFields.goals?.length}/1600
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 4 }}>
            <Box>
              <Typography
                variant="h6"
                color={errors.reflection_culturally_save_practice ? 'red' : 'primary'}
                sx={{ mb: 2 }}
              >
                Culturally Safe Practice{' '}
                <Typography
                  variant={'caption'}
                  color={errors.reflection_culturally_save_practice ? 'red' : 'primary'}
                  fontWeight={400}
                >
                  (Required)
                </Typography>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ pb: 2, a: { textDecoration: 'underline' } }}>
                How will you ensure cultural safety for Aboriginal and Torres Strait Islander people
                in your scope of practice? A portion of your 50 hours CPD must address this subject
                annually. Recommended activities can be found{' '}
                <a
                  href="https://www.racgp.org.au/cpd/cpd-resources/member-resources/racgp-s-cpd-solution-for-culturally-safe-practice/introduction"
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>
                .
              </Typography>
              <Controller
                name="reflection_culturally_save_practice"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    {...register('reflection_culturally_save_practice', {
                      required: true,
                      minLength: 3
                    })}
                    error={!!error}
                    fullWidth={true}
                    id="outlined-textarea"
                    label="Your response"
                    multiline
                    inputProps={{ maxLength: 1600 }}
                    minRows={5}
                    maxRows={15}
                    variant="outlined"
                  />
                )}
              />
              <Typography component="p" variant="caption" align="right" sx={{ mt: 1 }}>
                {watchAllFields.reflection_culturally_save_practice?.length}/1600
              </Typography>
              <Accordion
                sx={{ boxShadow: 'none', display: 'contents' }}
                disableGutters
                expanded={expanded === 'accordion3'}
                onChange={handleChange('accordion3')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="primary" fontSize="small" />}
                  aria-controls="my-reflection-content"
                  id="my-reflection-header"
                  sx={{ display: 'inline-flex', p: 0, mt: '-30px' }}
                >
                  <Typography variant="subtitle2" color="primary">
                    Examples
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Typography variant="body2" sx={{ userSelect: 'none' }}>
                    Review any practice-level arrangements that support Aboriginal and Torres Strait
                    Islander patients. Consider any changes you need to make to your own practice in
                    this regard. Update knowledge on this topic, as relevant to your scope of
                    practice.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
          <Box sx={{ mt: 4 }}>
            <Box>
              <Typography
                variant="h6"
                color={errors.reflection_health_inequities ? 'red' : 'primary'}
                sx={{ mb: 2 }}
              >
                Health Inequities{' '}
                <Typography
                  variant={'caption'}
                  color={errors.reflection_health_inequities ? 'red' : 'primary'}
                  fontWeight={400}
                >
                  (Required)
                </Typography>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ pb: 2, a: { textDecoration: 'underline' } }}>
                How will you improve and apply your knowledge and understanding of Health Inequities
                to your scope of practice? A portion of your 50 hours CPD must address this subject
                annually. Recommended activities can be found{' '}
                <a
                  href="https://www.racgp.org.au/cpd/cpd-resources/member-resources/racgp-s-cpd-solution-for-addressing-health-inequit/introduction"
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>
                .
              </Typography>
              <Controller
                name="reflection_health_inequities"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    {...register('reflection_health_inequities', {
                      required: true,
                      minLength: 3
                    })}
                    error={!!error}
                    fullWidth={true}
                    id="outlined-textarea"
                    label="Your response"
                    multiline
                    inputProps={{ maxLength: 1600 }}
                    minRows={5}
                    maxRows={15}
                    variant="outlined"
                  />
                )}
              />
              <Typography component="p" variant="caption" align="right" sx={{ mt: 1 }}>
                {watchAllFields.reflection_health_inequities?.length}/1600
              </Typography>

              <Accordion
                sx={{ boxShadow: 'none', display: 'contents' }}
                disableGutters
                expanded={expanded === 'accordion-h-i'}
                onChange={handleChange('accordion-h-i')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="primary" fontSize="small" />}
                  aria-controls="my-reflection-health-inequities"
                  id="my-reflection-health-inequities"
                  sx={{ display: 'inline-flex', p: 0, mt: '-30px' }}
                >
                  <Typography variant="subtitle2" color="primary">
                    Examples
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Typography variant="body2" sx={{ userSelect: 'none' }}>
                    In the community that we serve, inevitably, health inequality exists but is
                    often hidden. While some health inequality is displayed to GPs, some is not. A
                    patient may not present because of stigma, shame, fear or cost. Consider how you
                    can ensure that your practice meetings include the issue of health inequality.
                    Think about how you can remove as many barriers as possible in your own clinical
                    work to minimise the risk of any health inequality continuing.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
          <Box sx={{ mt: 4 }}>
            <Box>
              <Typography
                variant="h6"
                color={errors.reflection_professionalism_ethical_practice ? 'red' : 'primary'}
                sx={{ mb: 2 }}
              >
                Professionalism and Ethical Practice{' '}
                <Typography
                  variant={'caption'}
                  color={errors.reflection_professionalism_ethical_practice ? 'red' : 'primary'}
                  fontWeight={400}
                >
                  (Required)
                </Typography>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ pb: 2, a: { textDecoration: 'underline' } }}>
                How will you improve and apply your knowledge and understanding of Professionalism
                and Ethical Practice to your scope of practice? How will you prioritise your own
                self-care and wellbeing? A portion of your 50 hours CPD must address this subject
                annually. Recommended activities can be found{' '}
                <a
                  href="https://www.racgp.org.au/cpd/cpd-resources/member-resources/racgp-s-cpd-solution-for-professionalism-and-ethic/introduction"
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>
                .
              </Typography>
              <Controller
                name="reflection_professionalism_ethical_practice"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    {...register('reflection_professionalism_ethical_practice', {
                      required: true,
                      minLength: 3
                    })}
                    error={!!error}
                    fullWidth={true}
                    id="outlined-textarea"
                    label="Your response"
                    multiline
                    inputProps={{ maxLength: 1600 }}
                    minRows={5}
                    maxRows={15}
                    variant="outlined"
                  />
                )}
              />
              <Typography component="p" variant="caption" align="right" sx={{ mt: 1 }}>
                {watchAllFields.reflection_professionalism_ethical_practice?.length}/1600
              </Typography>

              <Accordion
                sx={{ boxShadow: 'none', display: 'contents' }}
                disableGutters
                expanded={expanded === 'accordion-pep'}
                onChange={handleChange('accordion-pep')}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon color="primary" fontSize="small" />}
                  aria-controls="reflection-professionalism-ethical-practice"
                  id="reflection-professionalism-ethical-practice"
                  sx={{ display: 'inline-flex', p: 0, mt: '-30px' }}
                >
                  <Typography variant="subtitle2" color="primary">
                    Examples
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Typography variant="body2" sx={{ userSelect: 'none' }}>
                    Do you have your own GP? Do you connect regularly with your peers, taking
                    regular time off for yourself, etc. Read and review Medical Board newsletters
                    and policy documents. Read and review communications from your Medical Indemnity
                    provider and complete their professional development program. Read and review
                    relevant RACGP communications on this topic.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
          <Divider sx={{ mt: 4, mb: 2 }} />
          <Grid container>
            <Grid item xs={12} md={4}>
              <Typography
                variant="h6"
                color={errors.hours_spent ? 'red' : 'primary'}
                sx={{ mb: 2 }}
              >
                Time spent on my PDP{' '}
                <Typography
                  variant={'caption'}
                  color={errors.hours_spent ? 'red' : 'primary'}
                  fontWeight={400}
                >
                  (Required)
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2">
                    Log the time you have spent completing your PDP between 2-5 hours.
                  </Typography>
                  <Typography variant="body2">
                    The hours will be allocated to reviewing performance upon submission.
                  </Typography>
                </Box>
                <Box sx={{ display: 'block' }}>
                  <Controller
                    name="hours_spent"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        color="secondary"
                        {...field}
                        {...register('hours_spent', {
                          required: true,
                          min: 2,
                          max: 5,
                          setValueAs: (v: any) => nanToZero(Number.parseFloat(v)),
                          onBlur: () => {
                            roundField('hours_spent');
                          }
                        })}
                        error={!!error}
                        sx={{ width: '130px' }}
                        type={'number'}
                        label="Hours"
                        variant="outlined"
                        InputProps={{
                          inputProps: {
                            min: 2,
                            step: 0.5,
                            max: 5
                          }
                        }}
                      />
                    )}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.725rem', mt: 1 }}
                    color={errors.hours_spent ? 'red' : 'primary'}
                  >
                    2-5 hours per PDP
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end'
            }}
          >
            {pdpSubmitStatus === PdpStatus.PdpDraft && (
              <>
                <Button
                  variant="outlined"
                  sx={{ minWidth: '180px', mr: { xs: 0, sm: 2 }, mb: { xs: 2, sm: 0 } }}
                  onClick={handleSave}
                >
                  Save Draft
                </Button>
                <Button variant="contained" sx={{ minWidth: '180px' }} type="submit">
                  Submit PDP
                </Button>
              </>
            )}
            {pdpSubmitStatus === PdpStatus.PdpSubmitted && (
              <Button
                variant="contained"
                sx={{ minWidth: '180px', mr: 2 }}
                onClick={handleSave}
                type="button"
                disabled={!isValid}
              >
                Update
              </Button>
            )}
          </Box>
        </form>
        <Snackbar
          open={openSnack}
          autoHideDuration={2000}
          onClose={handleCloseSnack}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnack} severity="success" sx={{ width: '100%' }}>
            Form saved.
          </Alert>
        </Snackbar>
        <Dialog
          open={open}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          disableEscapeKeyDown={true}
        >
          <DialogContent>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DialogContentText id="alert-dialog-description">
                <CheckCircleOutlineIcon color={'secondary'} />
              </DialogContentText>
              <Box>
                <DialogContentText>{dialogTitle}</DialogContentText>
                <DialogContentText>{dialogDescription}</DialogContentText>
              </Box>
            </Box>
            <DialogActions sx={{ p: 0 }}>
              <Button variant="text" onClick={handleClose} sx={{ minWidth: 'initial' }}>
                Close
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </Paper>
    </>
  );
};
export { PDPForm2026Plus };
